import {
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
  ConnectableObservable,
} from 'rxjs';

import {
  first,
  publishReplay,
  map,
  shareReplay,
  distinctUntilChanged,
  mergeMap,
} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ViewConfig} from './viewconfig';
import {ResourceAttributeWithPH, ResourceWithPermissionHints} from '../resource';
import {DialogConfiguration} from '../../cache/uiconfiguration/dialogconfiguration';
import {CacheService} from '../../cache/cache.service';
import {
  DetailControl,
  DetailControlOption,
  DetailTabconfig,
  ValuesObject,
} from './detail-tabconfig';
import {LanguageService} from '../../language/language.service';
import {DisplayAttribute} from '../displayattribute';
import {ResourceService} from '../resource.service';
import {User} from '../../cache/currentuser/user';
import {ObjectTypeDescription} from '../../cache/schema/objecttypedescription';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';
import {FilterResult} from '../filterresult';
import * as convert from 'xml-js';
import {XmlToJsonConverterService} from '../../cache/xml-to-json-converter.service';

@Injectable()
export class DetailService {
  private resource: Observable<ResourceWithPermissionHints> = null;
  private locale: Observable<string> = null;
  private create: boolean = false;

  static BindingRegex: RegExp =
    /^\{Binding Source=([A-Za-z]+), Path=([a-zA-Z_:][a-zA-Z0-9-/._:]*)(, ?Mode=[A-Za-z]+)?\}$/;
  static KnownBindings: string[] = [
    'schema',
    'object',
    'rights',
    'localization',
    'user',
    'syncruledatasource',
  ];
  static LocaleBindings: string[] = ['schemaLocConfig'];

  private currentEndpoint: Endpoint;

  constructor(
    private xmlToJson: XmlToJsonConverterService,
    private cacheService: CacheService,
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private envConfig: EnvironmentConfigService,
  ) {
    // Subscribe to endpoint changes
    this.layoutService.endpoint$.subscribe(endpoint => {
      this.currentEndpoint = endpoint;
    });
  }

  public static stringAsBoolean(str: string): boolean {
    str = str.toLowerCase().trim();
    if (str === 'true') return true;
    if (str === 'false') return false;
    return null;
  }

  public static AsBoolean(strin: ValuesObject<any>): boolean {
    if (strin == null) {
      return null;
    }
    let str: any = strin.Values;
    if (str == null) {
      return null;
    }
    if (str === true) {
      return true;
    }
    if (str === false) {
      return false;
    }
    if (str === 0) {
      return false;
    }
    if (Array.isArray(str)) {
      return str.indexOf('Read') !== -1;
    }
    if (typeof str === 'string') {
      str = str.toLowerCase().trim();
      if (str === 'true') return true;
      if (str === 'false') return false;
    }
    return null;
  }

  public static isKnownBinding(source: string) {
    return DetailService.KnownBindings.indexOf(source) !== -1;
  }

  public static isLocaleBinding(source: string) {
    return DetailService.LocaleBindings.indexOf(source) !== -1;
  }

  public getBodyTabConfigOfResourceType(
    resourceType: Observable<string>,
    resourcein: Observable<ResourceWithPermissionHints>,
    localein: Observable<string>,
  ): Observable<ViewConfig> {
    return resourceType.pipe(
      mergeMap((resourceType: string) => {
        if (resourceType == null) {
          throw new Error('Resource or Resource.ObjectType is null');
        }
        return this.getBodyTabConfig(
          resourcein,
          localein,
          this.cacheService.getDialogConfigurationByObjectType(resourceType),
        );
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  public getAdvancedBodyTabConfigOfResourceType(
    resourceType: Observable<string>,
    resourcein: Observable<ResourceWithPermissionHints>,
    localein: Observable<string>,
  ): Observable<ViewConfig> {
    return resourceType.pipe(
      mergeMap((resourceType: string) => {
        if (resourceType == null) {
          throw new Error('Resource or Resource.ObjectType is null');
        }
        return this.getAdvancedBodyTabConfig(
          this.cacheService.getDisplayAttributesForResource('Resource'),
          this.cacheService.getDisplayAttributesForResource(resourceType),
          resourcein,
          localein,
          resourceType,
        );
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  public getBodyTabConfig(
    resourcein: Observable<ResourceWithPermissionHints>,
    localein: Observable<string>,
    dialogConf: Observable<DialogConfiguration>,
    create: boolean = false,
  ): Observable<ViewConfig> {
    // create needed to prevent GET requests to service when object is not yet created (e.g. get localized attributes)
    this.resource = resourcein;
    this.locale = localein;
    return dialogConf.pipe(
      map((dgConf: DialogConfiguration) => {
        if (dgConf == null) return null;
        return this.parseDialogConfig(dgConf, dgConf.objecttype, create);
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private getAdvancedBodyTabConfig(
    commonObs: Observable<DisplayAttribute[]>,
    attributesObs: Observable<DisplayAttribute[]>,
    resourcein: Observable<ResourceWithPermissionHints>,
    localein: Observable<string>,
    resourceType: string,
  ): Observable<ViewConfig> {
    return commonObs.pipe(
      mergeMap((common: DisplayAttribute[]) => {
        return attributesObs.pipe(
          mergeMap((attributes: DisplayAttribute[]) => {
            this.resource = resourcein;
            this.locale = localein;
            let attributList: DisplayAttribute[] = [];
            attributes.forEach((att: DisplayAttribute) => {
              let incommon: boolean = false;
              common.forEach((commonatt: DisplayAttribute) => {
                if (att.Name === commonatt.Name) {
                  incommon = true;
                }
              });
              if (!incommon) {
                attributList.push(att);
              }
            });
            return observableOf(
              this.generateAdvancedDialogConfig(common, attributList, resourceType),
            );
          }),
        );
      }),
    );
  }

  private generateAdvancedDialogConfig(
    commonattributes: DisplayAttribute[],
    attributes: DisplayAttribute[],
    resourceType: string,
  ): ViewConfig {
    let inputBtc = new ViewConfig();

    //add summary
    inputBtc.HasSummary = true;

    //add header
    inputBtc.setDisplayName(
      this.cacheService.getResources().pipe(
        map((resources: ObjectTypeDescription[]) => {
          let resName = 'Object';
          resources.forEach((res: ObjectTypeDescription) => {
            if (res.systemname === resourceType) {
              resName = res.displayname;
            }
            if (res.systemname === 'Resource' && resName == 'Object') {
              resName = res.displayname;
            }
          });
          return resName;
        }),
      ),
    );

    //add common attributes
    let common = new DetailTabconfig();
    common.setName(observableOf(new ValuesObject('Common Attributes')));
    common.setCaption(observableOf(new ValuesObject('Common Attributes')));
    common.setVisible(observableOf(new ValuesObject('true')));
    common.setEnabled(observableOf(new ValuesObject('true')));
    common.setIsHeader(observableOf(new ValuesObject('false')));
    common.setDescription(observableOf(new ValuesObject('Common Attributes')));
    common = this.generateAdvancedControlsConfig(commonattributes, resourceType, common);

    //add extended attributes
    let extended = new DetailTabconfig();
    extended.setName(observableOf(new ValuesObject('Extended Attributes')));
    extended.setCaption(observableOf(new ValuesObject('Extended Attributes')));
    extended.setVisible(observableOf(new ValuesObject('true')));
    extended.setEnabled(observableOf(new ValuesObject('true')));
    extended.setIsHeader(observableOf(new ValuesObject('false')));
    extended.setDescription(observableOf(new ValuesObject('Common Attributes')));
    extended = this.generateAdvancedControlsConfig(attributes, resourceType, extended);

    let tempList = observableOf([common, extended]);
    inputBtc.setObjectType(resourceType);
    inputBtc.setDetails(tempList);
    return inputBtc;
  }

  private parseDialogConfig(
    dgConf: DialogConfiguration,
    resourceType: string,
    create: boolean = false,
  ): ViewConfig {
    this.create = create;

    let inputBtc = new ViewConfig();
    if (dgConf == null || dgConf.configuration == null) {
      throw new Error('DialogConfiguration or DialogConfiguration.configuration is null');
    }

    // RCDC XML from MIM, or the equivalent JSON document from the Identity Director Service
    const jsonConfig = this.xmlToJson.convertConfiguration(dgConf.configuration);

    let confObj: any = jsonConfig;
    if (
      confObj['my:ObjectControlConfiguration'] == null ||
      confObj['my:ObjectControlConfiguration']['my:Panel'] == null
    ) {
      throw new Error('my:ObjectControlConfiguration or my:Panel does not exist');
    }
    let panel: any = confObj['my:ObjectControlConfiguration']['my:Panel'];
    if (panel['my:Grouping'] == null) throw new Error('my:Grouping does not exist');
    let grouping: any[] = panel['my:Grouping'];
    let tempList: Observable<DetailTabconfig[]> = observableOf([]);
    grouping.forEach(group => {
      if (group['@my:IsSummary'] === 'true') {
        // skip summaries
        inputBtc.HasSummary = true;
        inputBtc.setSummaryFilteredAttributes(
          this.parseSummaryControlConfig(group, dgConf, resourceType),
        );
        return;
      }
      let tc = new DetailTabconfig();
      tc.setName(this.resolveAll(group['@my:Name'], dgConf, resourceType));
      tc.setCaption(this.resolveAll(group['@my:Caption'], dgConf, resourceType));
      if (group['@my:Visible'] == null) {
        tc.setVisible(observableOf(new ValuesObject('true')));
      } else {
        tc.setVisible(this.resolveAll(group['@my:Visible'], dgConf, resourceType));
      }
      if (group['@my:Enabled'] == null) {
        tc.setEnabled(observableOf(new ValuesObject('true')));
      } else {
        tc.setEnabled(this.resolveAll(group['@my:Enabled'], dgConf, resourceType));
      }
      if (group['@my:IsHeader'] == null) {
        tc.setIsHeader(observableOf(new ValuesObject('false')));
      } else {
        tc.setIsHeader(this.resolveAll(group['@my:IsHeader'], dgConf, resourceType));
      }

      tc.setDescription(this.resolveAll(group['@my:Description'], dgConf, resourceType));
      tc = this.parseControlsConfig(group, dgConf, resourceType, tc, inputBtc);
      tempList = tempList.pipe(
        mergeMap((list: DetailTabconfig[]) =>
          tc.IsHeader.pipe(
            map((bool: boolean) => {
              if (bool == null || bool != true) {
                list.push(tc);
              }
              return list;
            }),
          ),
        ),
      );
    });
    inputBtc.setObjectType(resourceType);
    inputBtc.setDetails(tempList);
    return inputBtc;
  }

  private generateAdvancedControlsConfig(
    attributes: DisplayAttribute[],
    resourceType: string,
    tc: DetailTabconfig,
  ): DetailTabconfig {
    let controls: DetailControl[] = [];

    attributes.forEach((attribute: DisplayAttribute) => {
      let detailControl = new DetailControl();
      detailControl.setName(observableOf(new ValuesObject(attribute.Name)));
      detailControl.setTypeName(observableOf(new ValuesObject('UocLabel')));
      if (
        this.currentEndpoint?.isCloud() &&
        resourceType === this.envConfig.getObjectTypeName('Request') &&
        attribute.Name === this.envConfig.getAttributeName('ObjectID')
      ) {
        attribute.DataType = 'String'; // we dont want a identitypicker to itself
      }
      switch (attribute.DataType) {
        default:
          detailControl.setTypeName(observableOf(new ValuesObject('UocLabel')));
          break;
        case 'Binary':
          if (attribute.Multivalued != true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocFileUpload')));
          }
          break;
        case 'Boolean':
          if (attribute.Multivalued != true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocCheckBox')));
          }
          break;
        case 'DateTime':
          if (attribute.Multivalued != true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocDateTimeControl')));
          }
          break;
        case 'Integer':
          if (attribute.Multivalued != true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocNumericBox')));
          }
          break;
        case 'Reference':
          if (attribute.Name !== this.envConfig.getAttributeName('ObjectID')) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocIdentityPicker')));
          }
          break;
        case 'String':
          if (attribute.Multivalued == true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocCommonMultiValueControl')));
          } else {
            detailControl.setTypeName(observableOf(new ValuesObject('UocTextBox')));
          }
          break;
        case 'Text':
          if (attribute.Multivalued != true) {
            detailControl.setTypeName(observableOf(new ValuesObject('UocTextBox')));
          }
          break;
      }
      if (attribute.Name === this.envConfig.getAttributeName('ObjectID')) {
        detailControl.setTypeName(observableOf(new ValuesObject('UocLabel')));
      }

      detailControl.setCaption(observableOf(new ValuesObject(attribute.Caption)));
      detailControl.setDescription(observableOf(new ValuesObject(attribute.Name)));
      detailControl.setEnabled(observableOf(new ValuesObject(true)));
      detailControl.setExpandArea(observableOf(new ValuesObject(false)));
      detailControl.setVisible(observableOf(new ValuesObject(true)));
      tc.pushControlVisible(detailControl.Visible);
      detailControl.setHint(observableOf(new ValuesObject(null)));
      detailControl.setRightsLevel(
        this.resolveAdvancedRights(
          '{Binding Source=rights, Path=' + attribute.Name + '}',
          resourceType,
        ).pipe(map(valObj => valObj.Values)),
      );
      detailControl.setProperties([]);

      let probs: Observable<ValuesObject<any>>[] = [];
      let probsValues: Observable<any>[] = [];

      const sourceConobs = this.resolveAdvancedRights(
        '{Binding Source=object, Path=' + attribute.Name + '}',
        resourceType,
      ).pipe(distinctUntilChanged(), publishReplay(1)) as ConnectableObservable<any>;
      detailControl.pushSubscription(sourceConobs.connect());
      probs['Source'] = sourceConobs;

      const targetConobs = this.resolveAdvancedRights(
        '{Binding Source=object, Path=' + attribute.Name + '}',
        resourceType,
      ).pipe(distinctUntilChanged(), publishReplay(1)) as ConnectableObservable<any>;
      detailControl.pushSubscription(targetConobs.connect());
      probs['Target'] = targetConobs;

      //datatype props
      switch (attribute.DataType) {
        default:
          break;
        case 'Binary':
          break;
        case 'Boolean':
          break;
        case 'DateTime':
          break;
        case 'Integer':
          break;
        case 'Reference':
          probs['UsageKeywords'] = observableOf(new ValuesObject('Global'));
          probs['AttributesToSearch'] = observableOf(
            new ValuesObject('DisplayName;Description;ObjectType'),
          );
          if (attribute.Multivalued == true) {
            probs['Mode'] = observableOf(new ValuesObject('MultipleResult'));
          }
          break;
        case 'String':
          break;
        case 'Text':
          break;
      }

      Object.keys(probs).forEach(key => {
        probsValues[key] = probs[key].pipe(
          map((valObj: ValuesObject<any>) => {
            if (valObj != null) {
              return valObj ? valObj.Values : null;
            } else {
              return null;
            }
          }),
          distinctUntilChanged(),
          shareReplay(1),
        );
      });
      detailControl.setProperties(probs);
      detailControl.setPropertyValues(probsValues);

      detailControl.setFinalEnabled(
        observableCombineLatest(
          detailControl.BasicEnabled,
          detailControl.getBooleanPropertyValues('ReadOnly'),
          detailControl.ModifyPermission,
          (enabled, readOnly, hasPermission) => {
            return enabled && (readOnly == null || readOnly == false) && hasPermission;
          },
        ),
      );
      controls.push(detailControl);
      tc.setHasChilds(
        tc.HasChilds.pipe(
          mergeMap((bool: boolean) =>
            detailControl.ReadPermission.pipe(
              map(permission => {
                return bool == true || permission;
              }),
            ),
          ),
        ),
      );
    });
    tc.Controls = controls;
    return tc;
  }

  private parseControlsConfig(
    group: any,
    dgConf: DialogConfiguration,
    resourceType: string,
    tc: DetailTabconfig,
    inputBtc: ViewConfig,
  ): DetailTabconfig {
    let controls: DetailControl[] = [];

    if (!(group['my:Control'] instanceof Array)) {
      group['my:Control'] = [group['my:Control']];
    }
    if (group['my:Control'] != null) {
      group['my:Control'].forEach(control => {
        if (control != null) {
          let detailControl = new DetailControl();
          detailControl.setName(this.resolveAll(control['@my:Name'], dgConf, resourceType));
          detailControl.setTypeName(this.resolveAll(control['@my:TypeName'], dgConf, resourceType));
          detailControl.setCaption(this.resolveAll(control['@my:Caption'], dgConf, resourceType));
          detailControl.setDescription(
            this.resolveAll(control['@my:Description'], dgConf, resourceType),
          );
          tc.IsHeader.pipe(first()).subscribe(isHeader => {
            if (isHeader) {
              inputBtc.setDisplayName(
                observableCombineLatest(
                  detailControl.Caption,
                  detailControl.Description,
                  (caption, description) => {
                    if (
                      !(description === null || description === undefined) &&
                      !(description.Values === null || description.Values === undefined) &&
                      description.Values !== ''
                    ) {
                      return description.Values;
                    } else if (
                      !(caption === null || caption === undefined) &&
                      !(caption.Values === null || caption.Values === undefined)
                    ) {
                      return caption.Values;
                    }
                    return '';
                  },
                ),
              );
            }
          });

          if (control['@my:Enabled'] == null) {
            detailControl.setEnabled(observableOf(new ValuesObject(true)));
          } else {
            detailControl.setEnabled(this.resolveAll(control['@my:Enabled'], dgConf, resourceType));
          }
          if (control['@my:ExpandArea'] == null) {
            detailControl.setExpandArea(observableOf(new ValuesObject(false)));
          } else {
            detailControl.setExpandArea(
              this.resolveAll(control['@my:ExpandArea'], dgConf, resourceType),
            );
          }
          if (control['@my:Visible'] == null) {
            detailControl.setVisible(observableOf(new ValuesObject(true)));
            tc.pushControlVisible(detailControl.Visible);
          } else {
            detailControl.setVisible(this.resolveAll(control['@my:Visible'], dgConf, resourceType));
            tc.pushControlVisible(detailControl.Visible);
          }

          if (control['@my:Hint'] == null) {
            detailControl.setHint(observableOf(new ValuesObject(null)));
          } else {
            detailControl.setHint(this.resolveAll(control['@my:Hint'], dgConf, resourceType));
          }
          if (control['@my:RightsLevel'] == null) {
            detailControl.setRightsLevel(observableOf(['Read', 'Modify', 'Add', 'Remove']));
          } else {
            detailControl.setRightsLevel(
              this.resolveAll(control['@my:RightsLevel'], dgConf, resourceType).pipe(
                map(valObj => valObj.Values),
              ),
            );
          }
          detailControl.setProperties([]);

          let probs: Observable<ValuesObject<any>>[] = [];
          let probsValues: Observable<any>[] = [];
          if (!(control['my:Properties'] === null || control['my:Properties'] === undefined)) {
            if (!(control['my:Properties']['my:Property'] instanceof Array)) {
              control['my:Properties']['my:Property'] = [control['my:Properties']['my:Property']];
            }

            control['my:Properties']['my:Property'].forEach(prop => {
              if (prop['@my:Name'] != null) {
                const conobs = this.resolveAll(prop['@my:Value'], dgConf, resourceType).pipe(
                  distinctUntilChanged(),
                  publishReplay(1),
                ) as ConnectableObservable<any>;
                detailControl.pushSubscription(conobs.connect());
                probs[prop['@my:Name']] = conobs;
              }
            });
          }

          // handle deprecated target attribute
          if (control['@my:TypeName'] === 'UocListView') {
            if (probs['TargetAttribute'] != null && probs['ListFilter'] == null) {
              const conobs = probs['TargetAttribute'].pipe(
                mergeMap((vo: ValuesObject<any>) => {
                  if (vo == null || vo.Values == null) return observableOf(null);
                  return this.resource.pipe(
                    map((res: ResourceWithPermissionHints) => {
                      if (
                        res == null ||
                        res[this.envConfig.getAttributeName('ObjectID')] == null ||
                        res[this.envConfig.getAttributeName('ObjectID')].Values == null ||
                        res[this.envConfig.getAttributeName('ObjectType')] == null ||
                        res[this.envConfig.getAttributeName('ObjectType')].Values == null
                      )
                        return null;
                      const filter =
                        '/' +
                        res[this.envConfig.getAttributeName('ObjectType')].Values +
                        '[' +
                        this.envConfig.getAttributeName('ObjectID') +
                        "='" +
                        res[this.envConfig.getAttributeName('ObjectID')].Values +
                        "']/" +
                        vo.Values;
                      const newVo = vo.clone();
                      newVo.Values = filter;
                      newVo.BindingSource = 'object';
                      newVo.BindingPath = vo.Values;
                      return newVo;
                    }),
                  );
                }),
                distinctUntilChanged(),
                publishReplay(1),
              );
              detailControl.pushSubscription(conobs.connect());
              probs['ListFilter'] = conobs;
            }
          }

          Object.keys(probs).forEach(key => {
            probsValues[key] = probs[key].pipe(
              map((valObj: ValuesObject<any>) => {
                if (valObj != null) {
                  return valObj ? valObj.Values : null;
                } else {
                  return null;
                }
              }),
              distinctUntilChanged(),
              shareReplay(1),
            );
          });
          detailControl.setProperties(probs);
          detailControl.setPropertyValues(probsValues);

          if (control['my:Options'] != null && control['my:Options']['my:Option'] != null) {
            detailControl.Options = [];
            if (!(control['my:Options']['my:Option'] instanceof Array)) {
              control['my:Options']['my:Option'] = [control['my:Options']['my:Option']];
            }
            control['my:Options']['my:Option'].forEach(option => {
              let ctrlOption: DetailControlOption = new DetailControlOption();
              ctrlOption.setCaption(this.resolveAll(option['@my:Caption'], dgConf, resourceType));
              ctrlOption.setHint(this.resolveAll(option['@my:Hint'], dgConf, resourceType));

              // Prepare Value
              let sourceObs: Observable<ValuesObject<any>> = null;
              if (detailControl.propertyExists('Source')) {
                sourceObs = detailControl.getProperty('Source');
              } else if (detailControl.propertyExists('SelectedValue')) {
                sourceObs = detailControl.getProperty('SelectedValue');
              }
              let dataTypeObs: Observable<string> = observableOf(null);
              if (sourceObs == null) {
                //No source found - string datatype assumed
              } else {
                dataTypeObs = sourceObs.pipe(
                  mergeMap((vo: ValuesObject<any>) => {
                    if (vo != null && vo.BindingSource === 'object') {
                      return this.cacheService
                        .getDisplayAttribute(vo.BindingPath, resourceType)
                        .pipe(map((da: DisplayAttribute) => da.DataType));
                    }
                    return observableOf(null);
                  }),
                );
              }

              const optionValueObs: Observable<ValuesObject<any>> = this.resolveAll(
                option['@my:Value'],
                dgConf,
                resourceType,
              );

              let valueObs = observableCombineLatest(
                dataTypeObs,
                optionValueObs,
                (dataType, optValue) => {
                  if (dataType == null) {
                    return optValue;
                  }

                  switch (dataType) {
                    default:
                    case 'String':
                    case 'Text':
                      return optValue;
                    case 'Integer':
                      let numTmp: number = parseInt(optValue.Values);
                      if (isNaN(numTmp)) {
                        return new ValuesObject(null);
                      }
                      return new ValuesObject(numTmp);
                    case 'DateTime':
                      //TODO test datetime
                      return new ValuesObject(new Date(optValue.Values));
                    case 'Boolean':
                      return new ValuesObject(DetailService.stringAsBoolean(optValue.Values));
                  }
                },
              );
              ctrlOption.setValue(valueObs);
              detailControl.Options.push(ctrlOption);
            });
          }
          detailControl.setFinalEnabled(
            observableCombineLatest(
              detailControl.BasicEnabled,
              detailControl.getBooleanPropertyValues('ReadOnly'),
              detailControl.ModifyPermission,
              (enabled, readOnly, hasPermission) => {
                return enabled && (readOnly == null || readOnly == false) && hasPermission;
              },
            ),
          );
          controls.push(detailControl);
          tc.setHasChilds(
            tc.HasChilds.pipe(
              mergeMap((bool: boolean) =>
                detailControl.ReadPermission.pipe(
                  map(permission => {
                    return bool == true || permission;
                  }),
                ),
              ),
            ),
          );
        }
      });
    }
    tc.Controls = controls;
    return tc;
  }

  private parseSummaryControlConfig(
    group: any,
    dgConf: DialogConfiguration,
    resourceType: string,
  ): Observable<string[]> {
    let controls: DetailControl[] = [];

    if (!(group['my:Control'] instanceof Array)) {
      group['my:Control'] = [group['my:Control']];
    }
    if (group['my:Control'] != null) {
      group['my:Control'].forEach(control => {
        if (control != null) {
          if (control['@my:TypeName'] === 'UocHtmlSummary') {
            if (!(control['my:Properties'] === null || control['my:Properties'] === undefined)) {
              if (!(control['my:Properties']['my:Property'] instanceof Array)) {
                control['my:Properties']['my:Property'] = [control['my:Properties']['my:Property']];
              }

              control['my:Properties']['my:Property'].forEach(prop => {
                if (prop['@my:Name'] != null && prop['@my:Name'] === 'FilteredAttributes') {
                  return this.resolveAll(prop['@my:Value'], dgConf, resourceType).pipe(
                    map((vo: ValuesObject<any>) => {
                      if (vo.Values instanceof Array) {
                        return vo.Values;
                      } else {
                        return [vo.Values];
                      }
                    }),
                    distinctUntilChanged(),
                    publishReplay(1),
                  );
                }
              });
            }
          }
        }
      });
    }
    return observableOf([]);
  }

  private resolveAll(
    value: any,
    dgConf: DialogConfiguration,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    if (!(typeof value === 'string')) return observableOf(new ValuesObject(value));

    const EnvironmentRegex: RegExp =
      /%(LoginID|LoginDomain|Today|ObjectID|FromToday_([0-9]{1,3})|Attribute_([a-zA-Z_:][a-zA-Z0-9-._:]*))%/g;

    const partsObs: Observable<string>[] = [];
    let lastIndex = 0;
    let matches: RegExpExecArray = EnvironmentRegex.exec(value);
    while (matches != null) {
      let beforeStr = value.substring(lastIndex, EnvironmentRegex.lastIndex - matches[0].length);
      partsObs.push(observableOf(beforeStr));
      partsObs.push(this.resolveEnvironmentVariable(matches));
      lastIndex = EnvironmentRegex.lastIndex;
      matches = EnvironmentRegex.exec(value);
    }
    let endString = value.substring(lastIndex);
    partsObs.push(observableOf(endString));

    return observableCombineLatest(partsObs).pipe(
      mergeMap((parts: string[]) => {
        return this.resolveAfterEnv(parts.join(''), dgConf, resourceType);
      }),
    );
  }

  private resolveAdvancedRights(value: any, resourceType: string): Observable<ValuesObject<any>> {
    if (!(typeof value === 'string')) return observableOf(new ValuesObject(value));

    return this.resolveAdvancedAfterEnv(value, resourceType);
  }

  private resolveAdvancedAfterEnv(value: any, resourceType: string): Observable<ValuesObject<any>> {
    let matches: RegExpExecArray = DetailService.BindingRegex.exec(value);
    if (matches != null) {
      // it is a binding
      return this.resolveAdvancedBindingDirect(matches[1], matches[2], resourceType);
    }
    return observableOf(new ValuesObject(value));
  }

  public resolveAdvancedBindingDirect(
    source: string,
    path: string,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    return this.resolveBindingOfResource(this.resource, source, path, resourceType).pipe(
      distinctUntilChanged(
        (p: ValuesObject<any>, q: ValuesObject<any>) =>
          p.Values === q.Values && p.SpecialObject === q.SpecialObject,
      ),
    );
  }

  private resolveAfterEnv(
    value: string,
    dgConf: DialogConfiguration,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    const ExpressionRegex: RegExp = /^%EXP_(.+)_END%$/;
    const SymbolRegex: RegExp = /%SYMBOL_([A-Za-z0-9]+)_END%/;
    const FilterRegex: RegExp =
      /^%FILTER_\{Binding Source=([A-Za-z]+), Path=([a-zA-Z_:][a-zA-Z0-9-/._:]*)(:?, ?Mode=[A-Za-z]+)?\}_END%$/;
    const SchemaObjectsRegex: RegExp =
      /^%SCHEMAOBJECTS_\{Binding Source=([A-Za-z]+), Path=([a-zA-Z_:][a-zA-Z0-9-/._:]*)(:?, ?Mode=[A-Za-z]+)?\}_END%$/;

    let matches: RegExpExecArray = DetailService.BindingRegex.exec(value);
    if (matches != null) {
      // it is a binding
      return this.resolveBindingDirect(matches[1], matches[2], resourceType);
    }

    matches = SymbolRegex.exec(value);
    if (matches != null) {
      // it is a symbol
      return observableOf(
        new ValuesObject(LanguageService.resolveDialogSymbol(matches[1], dgConf)),
      );
    }

    matches = ExpressionRegex.exec(value);
    if (matches != null) {
      // it is an expression
      let exp = this.resolveExpression(matches[1], resourceType);
      if (exp != null) {
        return exp;
      }
      return observableOf(null);
    }

    matches = FilterRegex.exec(value);
    if (matches != null) {
      return this.resolveFilter(matches[1], matches[2], resourceType);
    }

    matches = SchemaObjectsRegex.exec(value);
    if (matches != null) {
      return this.resolveSchemaObjects(matches[1], matches[2], resourceType);
    }

    return observableOf(new ValuesObject(value));
  }

  //<editor-fold desc="Resolve Bindings">
  private resolveBinding(value: string, resourceType: string): Observable<ValuesObject<any>> {
    let matches: RegExpExecArray = DetailService.BindingRegex.exec(value);
    if (matches != null) {
      let source: string = matches[1];
      let path: string = matches[2];
      return this.resolveBindingDirect(source, path, resourceType);
    }
    return observableOf(new ValuesObject(value));
  }

  public resolveBindingDirect(
    source: string,
    path: string,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    return this.resolveBindingOfResource(this.resource, source, path, resourceType).pipe(
      distinctUntilChanged(
        (p: ValuesObject<any>, q: ValuesObject<any>) =>
          p.Values === q.Values && p.SpecialObject === q.SpecialObject,
      ),
    );
  }

  public resolveBindingOfResource(
    resource: Observable<ResourceWithPermissionHints>,
    source: string,
    path: string,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    if (source === 'object') {
      return resource.pipe(
        mergeMap((res: ResourceWithPermissionHints) => {
          const splitterIndex: number = path.indexOf('/');
          if (splitterIndex !== -1) {
            const path1: string = path.substring(0, splitterIndex);
            const path2: string = path.substring(splitterIndex + 1);

            if (res[path1] == null || res[path1].Values == null) {
              console.warn('Resource Attribute at ' + path1 + ' is null. Cannot read values.');
              return observableOf(new ValuesObject(null));
            }

            return this.resourceService.getResourceWithPermissionHints(res[path1].Values).pipe(
              map((otherRes: ResourceWithPermissionHints) => {
                if (otherRes[path2] == null) {
                  console.error(
                    'Other Resource Attribute at ' + path + ' is null. Cannot read values.',
                  );
                  return null;
                } else {
                  let resPH: ResourceAttributeWithPH<any> = otherRes[path2];
                  return new ValuesObject(
                    resPH.Values,
                    resPH.PermissionHint,
                    source,
                    path,
                    null,
                    null,
                    resPH.SpecialObject,
                  );
                }
              }),
            );
          } else if (res[path] == null) {
            console.error('Resource Attribute at ' + path + ' is null. Cannot read values.');
            return observableOf(null);
          } else {
            let resPH: ResourceAttributeWithPH<any> = res[path];
            return this.cacheService.getDisplayAttribute(path, resourceType).pipe(
              map((da: DisplayAttribute) => {
                const formatSave = (value: any): any => {
                  if (value == null) return value;
                  if (
                    da != null &&
                    da.Multivalued &&
                    !Array.isArray(value) &&
                    typeof value !== 'object'
                  ) {
                    return [value];
                  } else {
                    return value;
                  }
                };
                return new ValuesObject(
                  resPH.Values,
                  resPH.PermissionHint,
                  source,
                  path,
                  null,
                  formatSave,
                  resPH.SpecialObject,
                );
              }),
            );
          }
        }),
      );
    } else if (source === 'schema') {
      const schemaPathRegex: RegExp = /([a-zA-Z_:][a-zA-Z0-9-_:]*)\.([a-zA-Z_:][a-zA-Z0-9-_:]*)/;
      let pathMatches: RegExpExecArray = schemaPathRegex.exec(path);
      if (pathMatches == null || pathMatches.length !== 3) return observableOf(null);
      return this.cacheService.getDisplayAttribute(pathMatches[1], resourceType).pipe(
        map((da: DisplayAttribute) => {
          if (pathMatches[2].toLowerCase() === 'localizedallowedvalues') {
            if (
              !(
                DisplayAttribute.getProperty(da, 'StringRegex') === null ||
                DisplayAttribute.getProperty(da, 'StringRegex') === undefined
              )
            ) {
              const regex: string = DisplayAttribute.getProperty(da, 'StringRegex').substring(
                2,
                DisplayAttribute.getProperty(da, 'StringRegex').indexOf(')'),
              );
              return new ValuesObject(regex.split('|'), null, source, path);
            } else {
              return new ValuesObject([], null, source, path);
            }
          } else {
            return new ValuesObject(
              DisplayAttribute.getProperty(da, pathMatches[2]),
              null,
              source,
              path,
            );
          }
        }),
      );
    } else if (source === 'rights') {
      return resource.pipe(
        map((res: ResourceWithPermissionHints) => {
          if (res[path] == null) {
            console.error(
              'Resource Attribute at ' + path + ' is null. Cannot read permission hint.',
            );
            return new ValuesObject(null);
          }
          return new ValuesObject(
            res[path].PermissionHint,
            null,
            source,
            path,
            null,
            null,
            res[path].SpecialObject,
          );
        }),
      );
    } else if (source === 'localization') {
      return this.locale.pipe(
        map(val => {
          if (val != null && val.length > 0) {
            return val;
          }
          return null;
        }),
        mergeMap((selectedLocale: string) => {
          return this.resource.pipe(
            map(res => res[this.envConfig.getAttributeName('ObjectID')].Values),
            distinctUntilChanged(),
            mergeMap((resID: string) => {
              let returnObs: Observable<ValuesObject<any>>;
              if (selectedLocale != null && selectedLocale.length > 0) {
                if (this.create) {
                  returnObs = observableOf(
                    new ValuesObject(
                      null,
                      ['Add', 'Create', 'Modify', 'Read', 'Remove'],
                      source,
                      path,
                      selectedLocale,
                    ),
                  );
                } else {
                  returnObs = this.resourceService
                    .getResourceWithPermissionHintsAndLanguage(resID, selectedLocale)
                    .pipe(
                      first(),
                      map((res: ResourceWithPermissionHints) => {
                        if (res[path] == null) {
                          console.error(
                            'Resource Attribute at ' +
                              path +
                              ' is null. Cannot read permission hint.',
                          );
                          return null;
                        }
                        let resPH: ResourceAttributeWithPH<any> = res[path];
                        return new ValuesObject(
                          resPH.Values,
                          resPH.PermissionHint,
                          source,
                          path,
                          selectedLocale,
                          null,
                          resPH.SpecialObject,
                        );
                      }),
                    );
                }
              } else {
                returnObs = observableOf(new ValuesObject(null, null, source, path));
              }
              return returnObs;
            }),
          );
        }),
      );
    } else if (source === 'user') {
      return this.cacheService.getCurrentUser().pipe(
        mergeMap((currentUser: User) =>
          this.resourceService.getResourceWithPermissionHints(currentUser.id).pipe(
            map((res: ResourceWithPermissionHints) => {
              if (res[path] == null) {
                console.warn(
                  'Resource Attribute at ' + path + ' is null. Cannot read permission hint.',
                );
                return null;
              }
              let resPH: ResourceAttributeWithPH<any> = res[path];
              return new ValuesObject(
                resPH.Values,
                resPH.PermissionHint,
                source,
                path,
                null,
                null,
                resPH.SpecialObject,
              );
            }),
          ),
        ),
      );
    } else if (source === 'syncruledatasource') {
      return this.resourceService
        .searchResources(
          '/' + this.envConfig.getObjectTypeName('MVData'),
          this.envConfig.getAttributeName('ObjectID'),
          1,
        )
        .pipe(
          mergeMap((fr: FilterResult) => {
            if (fr == null || fr.Results.length < 1) return observableOf(null);
            const id = fr.Results[0][this.envConfig.getAttributeName('ObjectID')];
            if (id == null) return observableOf(null);
            return this.resourceService.getResourceWithPermissionHints(id).pipe(
              map((res: ResourceWithPermissionHints) => {
                if (res == null) {
                  console.warn('Resource for syncruledatasource is null.');
                  return null;
                }

                if (res[path] == null) {
                  console.warn(
                    'SyncRuleDatasource Attribute ' + path + ' is null. Cannot read values.',
                  );
                  return null;
                }

                const resPH: ResourceAttributeWithPH<any> = res[path];
                return new ValuesObject(
                  resPH.Values,
                  resPH.PermissionHint,
                  source,
                  path,
                  null,
                  null,
                  resPH.SpecialObject,
                );
              }),
            );
          }),
        );
    } else {
      return observableOf(new ValuesObject(null, null, source, path));
    }
  }

  //</editor-fold>

  //<editor-fold desc="Expression resolving">
  private resolveExpression(
    expression: string,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    const BindingBooleanRegex: RegExp = /^(\!){0,2}(\{Binding [^\}]+\})$/;
    const expWithoutString = this.removeStringsFromExpression(expression);

    if (expWithoutString.indexOf('+') !== -1) {
      // it is a concatenation expression
      return this.resolveExpressionConcat(expression, resourceType);
    } else if (expWithoutString.indexOf('==') !== -1 || expression.indexOf('!=') !== -1) {
      // we have a boolean equivalence check
      if (expWithoutString.indexOf(' AND ') !== -1 || expWithoutString.indexOf(' OR ') !== -1) {
        return this.resolveExpressionEquivalenceCheckWithLogicOperators(expression, resourceType);
      } else {
        return this.resolveExpressionEquivalenceCheck(expression, resourceType);
      }
    } else {
      let matches = BindingBooleanRegex.exec(expression);
      if (matches != null) {
        // it is a negation or simple expression
        return this.resolveExpressionBooleanBinding(expression, matches, resourceType);
      } else {
        // no case matches - throw error
        console.error('Could not resolve the following expression: ' + expression);
        return null;
      }
    }
  }

  private removeStringsFromExpression(expression: string): string {
    if (expression == null || expression === '') return '';
    let result = '';
    let strStart = null;
    for (let i = 0; i < expression.length; i++) {
      let c = expression.charAt(i);
      if (strStart === null) {
        if (c === "'" || c === '"') {
          result += '{STRING}';
          strStart = c;
        } else {
          result += c;
        }
      } else {
        if (c === '\\' && i + 1 < expression.length && expression.charAt(i + 1) === strStart) {
          i++;
        } else if (c === strStart) {
          strStart = null;
        }
      }
    }

    if (strStart !== null) {
      throw new Error('String in expression not terminated correctly. Expression: ' + expression);
    }

    return result;
  }

  private resolveExpressionEquivalenceCheckWithLogicOperators(
    expression: string,
    resourceType: string,
  ): Observable<ValuesObject<boolean>> {
    const ExpressionLogicOperatorsRegex: RegExp =
      /^((?:(?:\{Binding [^\}]+\})|(?:'(?:[^']|\\')*'))\s*(?:==|\!=)\s*(?:(?:\{Binding [^\}]+\})|(?:'(?:[^']|\\')*')))(?:\s+(AND|OR)\s+(.+))?$/;
    let curExp: string = expression;
    const valueObs: Observable<boolean>[] = [];
    const operators: boolean[] = []; //true = AND, false = OR
    while (curExp != null) {
      let matches: RegExpExecArray = ExpressionLogicOperatorsRegex.exec(curExp);
      if (matches == null) {
        console.error('Could not compare the expression: ' + expression);
        return null;
      }
      /*
       * matches[1] = Binding
       * matches[2] = AND/OR
       * matches[3] = remaining string
       */

      const voOb = this.resolveExpressionEquivalenceCheck(matches[1], resourceType);
      if (voOb == null) {
        return null;
      }
      const bOb = voOb.pipe(map((vo: ValuesObject<boolean>) => (vo ? vo.Values : null)));
      valueObs.push(bOb);
      if (matches[2] != null) {
        operators.push(matches[2] === 'AND');
      }
      curExp = matches[3];
    }
    return observableCombineLatest(valueObs).pipe(
      map((values: boolean[]) => {
        let combinedValue: boolean = values[0];

        for (let i = 0; i < operators.length; i++) {
          if (operators[i]) {
            combinedValue = combinedValue && values[i + 1];
          } else {
            combinedValue = combinedValue || values[i + 1];
          }
        }

        return new ValuesObject(combinedValue);
      }),
    );
  }

  private resolveExpressionEquivalenceCheck(
    expression: string,
    resourceType: string,
  ): Observable<ValuesObject<boolean>> {
    const BindingComparisonRegex: RegExp =
      /^(?:(\{Binding [^\}]+\})|('.*'))\s*(==|\!=)\s*(?:(\{Binding [^\}]+\})|('.*'))$/;
    let matches = BindingComparisonRegex.exec(expression.trim());
    if (matches == null || matches.length !== 6) {
      console.error('Could not compare the expression: ' + expression);
      console.error(matches);
      return null;
    }
    // ({Binding ...}|'...') (==|!=) ({Binding ...}|'...')
    //  1             2       3       4             5

    let obs1: Observable<ValuesObject<any>>;
    let obs2: Observable<ValuesObject<any>>;

    if (matches[1] != null) {
      obs1 = this.resolveBinding(matches[1], resourceType);
    } else if (matches[2] != null) {
      obs1 = observableOf(new ValuesObject(DetailService.unescapeString(matches[2], true)));
    } else {
      console.error('Comparisons must use bindings or strings: expression: ' + expression);
      console.error(matches);
      return null;
    }

    if (matches[4] != null) {
      obs2 = this.resolveBinding(matches[4], resourceType);
    } else if (matches[5] != null) {
      obs2 = observableOf(new ValuesObject(DetailService.unescapeString(matches[5], true)));
    } else {
      console.error('Comparisons must use bindings or strings: expression: ' + expression);
      console.error(matches);
      return null;
    }

    return observableCombineLatest(obs1, obs2, (vo1: ValuesObject<any>, vo2: ValuesObject<any>) => {
      if (vo1 == null || vo2 == null) {
        console.error('Cannot handle comparison: A values object is null.');
        return null;
      }

      if (
        (Array.isArray(vo1.PermissionHint) && vo1.PermissionHint.indexOf('Read') === -1) ||
        (Array.isArray(vo2.PermissionHint) && vo2.PermissionHint.indexOf('Read') === -1)
      ) {
        console.error('Not enough permissions to read bindings');
        return null;
      }

      let val1: any = null,
        val2: any = null;
      let val1BooleanType: boolean = false,
        val2BooleanType: boolean = false;
      if (typeof vo1.Values === 'boolean') {
        val1 = vo1.Values;
        val1BooleanType = true;
      } else if (typeof vo1.Values === 'string') {
        val1 = DetailService.stringAsBoolean(vo1.Values);
        val1BooleanType = val1 != null;
        if (!val1BooleanType) {
          val1 = vo1.Values;
        }
      } else if (typeof vo1.Values === 'number') {
        val1 = '' + vo1.Values;
      } else if (typeof vo1.Values === 'object' && Array.isArray(vo1.Values)) {
        if (typeof vo1.Values[0] === 'boolean') {
          val1 = vo1.Values[0];
          val1BooleanType = true;
        } else if (typeof vo1.Values[0] === 'string') {
          val1 = DetailService.stringAsBoolean(vo1.Values[0]);
          val1BooleanType = val1 != null;
          if (!val2BooleanType) {
            val1 = vo1.Values[0];
          }
        }
        val1 = '' + vo1.Values[0];
      } else if (vo1.Values == null) {
        val1 = null;
      } else {
        console.error('Cannot handle comparison for type ' + typeof vo1.Values);
        return null;
      }

      if (typeof vo2.Values === 'boolean') {
        val2 = vo2.Values;
        val2BooleanType = true;
      } else if (typeof vo2.Values === 'string') {
        val2 = DetailService.stringAsBoolean(vo2.Values);
        val2BooleanType = val2 != null;
        if (!val2BooleanType) {
          val2 = vo2.Values;
        }
      } else if (typeof vo2.Values === 'number') {
        val2 = '' + vo2.Values;
      } else if (typeof vo2.Values === 'object' && Array.isArray(vo2.Values)) {
        if (typeof vo2.Values[0] === 'boolean') {
          val2 = vo2.Values[0];
          val2BooleanType = true;
        } else if (typeof vo2.Values[0] === 'string') {
          val2 = DetailService.stringAsBoolean(vo2.Values[0]);
          val2BooleanType = val2 != null;
          if (!val2BooleanType) {
            val2 = vo2.Values[0];
          }
        }
        val2 = '' + vo2.Values[0];
      } else if (vo2.Values == null) {
        val2 = null;
      } else {
        console.error('Cannot handle comparison for type ' + typeof vo2.Values);
        return null;
      }

      if (val1BooleanType !== val2BooleanType) {
        if (val1 !== null && val2 !== null) {
          console.error('Cannot compare a boolean value with a non boolean value.');
        }
        return null;
      }

      if (val1 === '') val1 = null;
      if (val2 === '') val2 = null;

      if (matches[3] === '==') {
        return new ValuesObject<boolean>(val1 === val2);
      } else {
        return new ValuesObject<boolean>(val1 !== val2);
      }
    });
  }

  private resolveExpressionBooleanBinding(
    expression: string,
    matches: RegExpExecArray,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    return this.resolveBinding(matches[2], resourceType).pipe(
      map((vo: ValuesObject<any>) => {
        if (vo == null) return null;
        if (matches[1] === '!') {
          if (typeof vo.Values === 'boolean') {
            const newVo: ValuesObject<any> = vo.clone();
            newVo.Values = !vo.Values;
            return newVo;
          } else if (typeof vo.Values === 'string') {
            const negated: string = DetailService.negateBooleanString(vo.Values);
            if (negated != null) {
              const newVo: ValuesObject<any> = vo.clone();
              newVo.Values = negated;
              return newVo;
            } else {
              console.error(
                "Could not negate string binding '" + vo.Values + "' for expression: " + expression,
              );
              return null;
            }
          } else if (vo.Values == null) {
            const newVo: ValuesObject<any> = vo.clone();
            newVo.Values = true;
            return newVo;
          } else {
            console.error('Could not negate binding: ' + expression);
            console.error(vo);
            return null;
          }
        } else if (matches[1] === '!!') {
          if (typeof vo.Values === 'boolean') {
            const newVo: ValuesObject<any> = vo.clone();
            newVo.Values = vo.Values === true;
            return newVo;
          } else {
            console.error(
              "Double-negate non boolean value '" + vo.Values + "' for expression: " + expression,
            );
            return null;
          }
        } else {
          return vo;
        }
      }),
    );
  }

  private static negateBooleanString(bool: string): string {
    if (bool === 'True') return 'False';
    if (bool === 'TRUE') return 'FALSE';
    if (bool === 'true') return 'false';
    if (bool === 'False') return 'True';
    if (bool === 'FALSE') return 'TRUE';
    if (bool === 'false') return 'true';
    return null;
  }

  private static unescapeString(str: string, removeFirstAndLast: boolean = false): string {
    if (removeFirstAndLast) {
      str = str.substring(1, str.length - 1);
    }
    return str.replace(/([^\\]|^)\\(['"])/g, '$1$2').replace(/\\\\/g, '\\\\');
  }

  private resolveExpressionConcat(
    expression: string,
    resourceType: string,
  ): Observable<ValuesObject<any>> {
    let mustApply = false;
    if (expression.startsWith('MUSTAPPLY_')) {
      expression = expression.substring(10);
      mustApply = true;
    }

    class DoesntApply {}

    let curExp: string = expression;
    let concatObs: Observable<any>[] = [];

    const BindingAtStart: RegExp =
      /^\{Binding Source=([A-Za-z]+), Path=([a-zA-Z_:][a-zA-Z0-9-/._:]*)(?:, Mode=[A-Za-z]+)?\}\s*(?:\+\s*(.*)|$)/;
    const StringAtStart: RegExp = /^\'((?:[^\\]|\\'|\\\\)*?)\'\s*(?:\+\s*(.*)|$)/;

    while (curExp != null && curExp.length > 0) {
      let matches = BindingAtStart.exec(curExp);
      if (matches != null) {
        const obs = this.resolveBindingDirect(matches[1], matches[2], resourceType).pipe(
          map((vo: ValuesObject<any>) => {
            if (vo == null) {
              return mustApply ? new DoesntApply() : '';
            } else if (
              Array.isArray(vo.PermissionHint) &&
              vo.PermissionHint.indexOf('Read') === -1
            ) {
              console.error('Not enough permission to read binding of concat expression.');
              return mustApply ? new DoesntApply() : '';
            } else if (vo.Values == null) {
              return mustApply ? new DoesntApply() : '';
            } else {
              return vo.Values;
            }
          }),
        );
        concatObs.push(obs);
        curExp = matches[3];
        continue;
      }

      matches = StringAtStart.exec(curExp);
      if (matches != null) {
        concatObs.push(observableOf(DetailService.unescapeString(matches[1])));
        curExp = matches[2];
        continue;
      }

      console.error('Cannot resolve expression: ' + expression + '; parsed concat until ' + curExp);
      return null;
    }

    return observableCombineLatest(concatObs).pipe(
      map((arr: any[]) => {
        if (arr != null) {
          if (mustApply) {
            for (let i = 0; i < arr.length; i++) {
              if (arr[i] instanceof DoesntApply) {
                return null;
              }
            }
          }
          return arr.join('');
        }

        return null;
      }),
      map((str: string) => new ValuesObject<string>(str)),
    );
  }

  //</editor-fold>

  private resolveEnvironmentVariable(matches: RegExpExecArray): Observable<string> {
    let variable: string = matches[1];

    switch (variable) {
      case 'LoginID':
        //currentUser.ObjectID
        return this.cacheService.getCurrentUser().pipe(map(u => (u ? u.id : null)));
      case 'LoginDomain':
        //currentUser.Domain
        return this.cacheService.getCurrentUser().pipe(map(u => (u ? u.domain : null)));
      case 'Today':
        return observableOf(DetailService.toIsoDateOnlyString(new Date()));
      case 'ObjectID':
        //this.resource ObjectID.Values
        return this.resource.pipe(
          map(r =>
            r
              ? r[this.envConfig.getAttributeName('ObjectID')]
                ? r[this.envConfig.getAttributeName('ObjectID')].Values
                : null
              : null,
          ),
        );
    }

    if (variable.startsWith('FromToday_')) {
      let d: Date = new Date();
      d.setDate(d.getDate() + parseInt(matches[2]));
      return observableOf(DetailService.toIsoDateOnlyString(d));
    }

    if (variable.startsWith('Attribute_')) {
      return this.resource.pipe(
        map(r => (r ? (r[matches[3]] ? r[matches[3]].Values : null) : null)),
      );
    }

    return observableOf(matches[0]);
  }

  static padNumber(num: number): string {
    if (num < 10) {
      return '0' + num;
    }
    return '' + num;
  }

  static toIsoDateOnlyString(date: Date): string {
    return (
      date.getUTCFullYear() +
      '-' +
      DetailService.padNumber(date.getUTCMonth() + 1) +
      '-' +
      DetailService.padNumber(date.getUTCDate()) +
      'T00:00:00.000'
    );
  }

  private resolveFilter(
    bindingSource: string,
    bindingPath: string,
    resourceType: string,
  ): Observable<ValuesObject<string>> {
    return this.resolveBindingDirect(bindingSource, bindingPath, resourceType).pipe(
      map((vo: ValuesObject<any>) => {
        if (vo == null) return null;
        let value: any;
        if (vo.Values == null || !(typeof vo.Values === 'string')) {
          value = null;
        } else {
          const filterRegExp: RegExp = /^.*<Filter [^>]+>([^<]+)<\/Filter>.*$/;
          let matches = filterRegExp.exec(vo.Values);
          if (matches == null) {
            value = null;
          } else {
            value = matches[1]
              .replace(/&apos;/g, "'")
              .replace(/&quot;/g, '"')
              .replace(/&gt;/g, '>')
              .replace(/&lt;/g, '<')
              .replace(/&amp;/g, '&');
          }
        }
        const newVo: ValuesObject<any> = vo.clone();
        newVo.Values = value;
        newVo.formatSave = (value: any) => {
          if (value == null || value === '') return null;
          // encode value
          value = value.replace(/[<>&'"]/g, function (c) {
            switch (c) {
              case '<':
                return '&lt;';
              case '>':
                return '&gt;';
              case '&':
                return '&amp;';
              case "'":
                return '&apos;';
              case '"':
                return '&quot;';
            }
          });
          value =
            '<Filter xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" Dialect="http://schemas.microsoft.com/2006/11/XPathFilterDialect" xmlns="http://schemas.xmlsoap.org/ws/2004/09/enumeration">' +
            value +
            '</Filter>';
          if (vo.formatSave != null && typeof vo.formatSave === 'function') {
            value = vo.formatSave(value);
          }
          return value;
        };
        return newVo;
      }),
    );
  }

  /*
    Function getFilter setFilter
    <Filter xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" Dialect="http://schemas.microsoft.com/2006/11/XPathFilterDialect" xmlns="http://schemas.xmlsoap.org/ws/2004/09/enumeration">/Person[(ObjectID = '6ba921cd-e932-4928-8329-8ca1bab35f12')]</Filter>

     */

  private resolveSchemaObjects(
    bindingSource: string,
    bindingPath: string,
    resourceType: string,
  ): Observable<ValuesObject<string[]>> {
    return this.resolveBindingDirect(bindingSource, bindingPath, resourceType).pipe(
      map((vo: ValuesObject<any>) => {
        if (vo == null) return null;
        let value: string[];
        if (vo.Values == null || !(typeof vo.Values === 'string')) {
          value = null;
        } else {
          const obj: any = convert.xml2js(vo.Values, {
            compact: true,
            alwaysArray: true,
            ignoreAttributes: true,
            elementNameFn: name => name.replace(/(?!xmlns)^.*:/, ''),
          });

          if (
            Array.isArray(obj.dsml) &&
            obj.dsml.length === 1 &&
            Array.isArray(obj.dsml[0]['directory-schema']) &&
            obj.dsml[0]['directory-schema'].length === 1 &&
            Array.isArray(obj.dsml[0]['directory-schema'][0].class)
          ) {
            value = [];
            obj.dsml[0]['directory-schema'][0].class.forEach(c => {
              if (
                Array.isArray(c.name) &&
                c.name.length >= 1 &&
                Array.isArray(c.name[0]._text) &&
                c.name[0]._text.length >= 1
              ) {
                value.push(c.name[0]._text[0]);
              }
            });
            value = value.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
          } else {
            value = null;
          }
        }
        const newVo: ValuesObject<any> = vo.clone();
        newVo.Values = value;
        return newVo;
      }),
    );
  }
}
